import React from "react";
import "./NoData.scss";

/* The list-is-empty scene: a stack of sheets with a magnifier over it. */
const EmptyArt = () => (
  <svg viewBox="0 0 160 120" role="img" aria-hidden="true">
    <defs>
      <linearGradient id="whNdSheet" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#eef4fb" />
      </linearGradient>
      <linearGradient id="whNdGlass" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#2b8fe0" />
        <stop offset="100%" stopColor="#0f9d63" />
      </linearGradient>
    </defs>

    <ellipse className="wh-nd-shadow" cx="80" cy="104" rx="42" ry="6" />

    <g className="wh-nd-float">
      <rect className="wh-nd-back" x="46" y="26" width="62" height="72" rx="9"
        transform="rotate(-8 77 62)" />
      <rect className="wh-nd-back wh-nd-back--2" x="52" y="24" width="62" height="72" rx="9"
        transform="rotate(5 83 60)" />

      <rect className="wh-nd-sheet" x="49" y="22" width="62" height="74" rx="10" />
      <rect className="wh-nd-line" x="59" y="38" width="42" height="5" rx="2.5" />
      <rect className="wh-nd-line" x="59" y="50" width="32" height="5" rx="2.5" />
      <rect className="wh-nd-line" x="59" y="62" width="37" height="5" rx="2.5" />
      <rect className="wh-nd-line wh-nd-line--faint" x="59" y="74" width="24" height="5" rx="2.5" />
    </g>

    <g className="wh-nd-glass">
      <circle className="wh-nd-glass-ring" cx="103" cy="72" r="18" />
      <circle className="wh-nd-glass-fill" cx="103" cy="72" r="18" />
      <line className="wh-nd-glass-stem" x1="116" y1="85" x2="128" y2="97" />
    </g>

    <circle className="wh-nd-dot" cx="36" cy="40" r="3" />
    <circle className="wh-nd-dot wh-nd-dot--2" cx="126" cy="34" r="2.5" />
    <circle className="wh-nd-dot wh-nd-dot--3" cx="34" cy="82" r="2" />
  </svg>
);

/* The not-finished-yet scene: a blueprint with two cogs turning on it. */
const BuildingArt = () => (
  <svg viewBox="0 0 160 120" role="img" aria-hidden="true">
    <defs>
      <linearGradient id="whNdPlan" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#eef4fb" />
      </linearGradient>
      <linearGradient id="whNdCog" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#2b8fe0" />
        <stop offset="100%" stopColor="#0f9d63" />
      </linearGradient>
    </defs>

    <ellipse className="wh-nd-shadow" cx="80" cy="104" rx="44" ry="6" />

    {/* the plan being worked on */}
    <g className="wh-nd-float">
      <rect className="wh-nd-sheet" x="34" y="24" width="92" height="70" rx="11" />

      {/* a sketched layout on it */}
      <rect className="wh-nd-plan" x="45" y="35" width="30" height="22" rx="4" />
      <rect className="wh-nd-plan wh-nd-plan--faint" x="45" y="63" width="30" height="18" rx="4" />
      <rect className="wh-nd-line" x="83" y="36" width="32" height="5" rx="2.5" />
      <rect className="wh-nd-line" x="83" y="47" width="24" height="5" rx="2.5" />
      <rect className="wh-nd-line wh-nd-line--faint" x="83" y="58" width="29" height="5" rx="2.5" />
    </g>

    {/* two cogs, turning against each other */}
    <g className="wh-nd-cog wh-nd-cog--big">
      <path className="wh-nd-cog-teeth" d="M111 62 l4.5 1.2 2.6-3.8 4 2.3-1.3 4.4 3.3 3.3 4.4-1.3 2.3 4-3.8 2.6 1.2 4.5-4.6.6-1.6 4.3-4.3-1.6-3.5 3.1-3.1-3.5-4.3 1.6-1.6-4.3-4.6-.6 1.2-4.5-3.8-2.6 2.3-4 4.4 1.3 3.3-3.3-1.3-4.4 4-2.3 2.6 3.8z" />
      <circle className="wh-nd-cog-hub" cx="115" cy="76" r="6" />
    </g>

    <g className="wh-nd-cog wh-nd-cog--small">
      <path className="wh-nd-cog-teeth" d="M46 78 l3.4.9 2-2.9 3 1.7-1 3.3 2.5 2.5 3.3-1 1.7 3-2.9 2 .9 3.4-3.5.5-1.2 3.2-3.2-1.2-2.6 2.3-2.3-2.6-3.2 1.2-1.2-3.2-3.5-.5.9-3.4-2.9-2 1.7-3 3.3 1 2.5-2.5-1-3.3 3-1.7 2 2.9z" />
      <circle className="wh-nd-cog-hub" cx="47" cy="88" r="4.5" />
    </g>

    <circle className="wh-nd-dot" cx="24" cy="42" r="3" />
    <circle className="wh-nd-dot wh-nd-dot--2" cx="138" cy="36" r="2.5" />
    <circle className="wh-nd-dot wh-nd-dot--3" cx="26" cy="70" r="2" />
  </svg>
);

const SCENES = {
  empty: EmptyArt,
  building: BuildingArt,
};

/* A single empty state for every list in the app. Callers can pass their own
   wording, and pick the scene that fits:
     <NoData title="No tickets yet" message="Raise one and it shows up here." />
     <NoData variant="building" title="Not built yet" message="…" />
   The artwork is inline SVG rather than a PNG, so it stays sharp, takes the
   theme's colours, and costs no extra request. */
const NoData = ({
  title = "Nothing here yet",
  message = "There is no data to show for this view right now.",
  variant = "empty",
}) => {
  const Art = SCENES[variant] || EmptyArt;

  return (
    <div className="wh-nodata" role="status">
      <div className="wh-nodata-art">
        <Art />
      </div>

      <h6 className="wh-nodata-title">{title}</h6>
      <p className="wh-nodata-message">{message}</p>
    </div>
  );
};

export default NoData;
